import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Check, ChevronDown, Globe } from "lucide-react";
import { SUPPORTED_LANGS, LANG_META, setLanguage, type SupportedLang } from "@/i18n";

export function LanguageSwitcher({
  align = "right",
  compact = false,
}: {
  align?: "left" | "right";
  compact?: boolean;
}) {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [hydrated, setHydrated] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const currentLanguage = hydrated ? i18n.language : "en";
  const languageLabel = hydrated ? t("nav.language") : i18n.getFixedT("en")("nav.language");
  const current = (SUPPORTED_LANGS.find((l) => currentLanguage?.startsWith(l)) ??
    "en") as SupportedLang;
  const meta = LANG_META[current];

  useEffect(() => setHydrated(true), []);

  useEffect(() => {
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const change = (lng: SupportedLang) => {
    void setLanguage(lng);
    window.localStorage.setItem("rt_lang", lng);
    // Also persist as a cookie so the server knows the language and can render
    // localized long-form content during SSR (no English flash on next load).
    if (typeof document !== "undefined") {
      document.cookie = `rt_lang=${lng}; path=/; max-age=31536000; SameSite=Lax`;
      document.documentElement.lang = lng;
    }
    setOpen(false);
  };

  if (SUPPORTED_LANGS.length <= 1) return null;

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        aria-label={languageLabel}
        onClick={() => setOpen((o) => !o)}
        className={`group inline-flex items-center gap-1.5 rounded-full hairline bg-card px-2.5 py-1.5 text-[12.5px] text-muted-foreground hover:text-foreground hover:bg-cream transition ${
          compact ? "" : "min-w-[58px]"
        }`}
      >
        <Globe className="h-3.5 w-3.5" strokeWidth={1.75} />
        <span className="font-medium uppercase tracking-wide text-[11px]">{current}</span>
        <ChevronDown
          className={`h-3 w-3 transition-transform ${open ? "rotate-180" : ""}`}
          strokeWidth={2}
        />
      </button>

      {open && (
        <div
          className={`absolute z-50 mt-2 w-44 rounded-xl hairline bg-background shadow-window overflow-hidden ${
            align === "right" ? "right-0" : "left-0"
          }`}
          role="listbox"
        >
          <div className="font-mono-eyebrow !text-[10px] px-3 pt-2.5 pb-1.5">{languageLabel}</div>
          <ul className="pb-1">
            {SUPPORTED_LANGS.map((lng) => {
              const m = LANG_META[lng];
              const active = lng === current;
              return (
                <li key={lng}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={active}
                    onClick={() => change(lng)}
                    className={`w-full flex items-center gap-2.5 px-3 py-2 text-left text-[13px] transition ${
                      active
                        ? "bg-cream text-foreground"
                        : "text-muted-foreground hover:bg-cream hover:text-foreground"
                    }`}
                  >
                    <span className="text-[14px] leading-none">{m.flag}</span>
                    <span className="flex-1 truncate">{m.native}</span>
                    <span className="text-[10px] uppercase font-mono text-muted-foreground/70">
                      {lng}
                    </span>
                    {active && <Check className="h-3 w-3 text-foreground" strokeWidth={2.5} />}
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

export default LanguageSwitcher;
